// Governance helpers (Ordre 22): decisions. The rules live on the server; this only guides the forms and words the answers.
import { hasPermission } from "./permissions";
import { getActiveZone, localToUtc, utcToLocal } from "./time";

const on = (user) => !!user?.features?.governance;
export const canSeeGovernance = (user) => on(user) && hasPermission(user, "governance:view");
export const canProposeDecision = (user) => canSeeGovernance(user) && hasPermission(user, "governance:create_decision");
export const canExportGovernance = (user) => canSeeGovernance(user) && hasPermission(user, "governance:export");
export const canApproveDecision = (user) => canSeeGovernance(user) && hasPermission(user, "governance:approve_decision");

export const newKey = () => `g-${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;

export const STATUS_LABELS = { proposed: "Proposée", adopted: "Adoptée", deferred: "Différée", cancelled: "Annulée" };
export const STATUS_TONES = { proposed: "sky", adopted: "emerald", deferred: "amber", cancelled: "slate" };

export const VIEW_TABS = [["to_adopt", "À adopter"], ["undecided", "Non tranchées"], ["adopted", "Adoptées"], ["all", "Toutes"]];
export const DEFAULT_VIEW = "undecided";

export const ACTION_LABELS = { adopt: "Adopter", defer: "Différer", cancel: "Annuler la décision", repropose: "Remettre en discussion" };
export const ACTION_HINTS = {
  adopt: "La décision est enregistrée et son contenu est figé. Rien ne s'exécute ailleurs : aucun paiement, aucun changement dans les autres modules.",
  defer: "La décision reste « non tranchée ». Elle ne déclenche rien et revient à votre attention à la date de revue.",
  cancel: "La décision est annulée. Son historique et ses liens sont conservés.",
  repropose: "La décision redevient une proposition à examiner.",
};
export const REASON_ACTIONS = ["defer", "cancel", "repropose"];
export const BLOCKED_MESSAGES = {
  self_review: "Vous avez proposé cette décision : une autre personne doit la trancher.",
  not_decider: "Seuls les décideurs nommés peuvent trancher cette décision.",
};

export const HISTORY_LABELS = {
  decision_created: "Décision proposée", decision_updated: "Décision modifiée", decision_adopt: "Décision adoptée", decision_defer: "Décision différée",
  decision_cancel: "Décision annulée", decision_repropose: "Remise en discussion", decision_superseded: "Remplacée par une nouvelle décision",
  task_linked: "Action liée", task_unlinked: "Action retirée",
  file_added: "Fichier joint", file_removed: "Fichier retiré",
};

export const LINK_KINDS = [
  ["expense", "Dépense"], ["record", "Document"], ["customer", "Dossier client"], ["ticket", "Ticket support"], ["product_item", "Demande produit"],
  ["purchase_request", "Demande d'achat"],
];
export const linkKindLabel = (kind) => (LINK_KINDS.find(([id]) => id === kind) || [])[1] || kind;

// Where a linked object opens (each module's own page); `state` is the router state a page consumes once.
export function linkTarget(kind, ref) {
  switch (kind) {
    case "expense": return { to: "/finance", state: { openExpense: ref } };
    case "record": return { to: `/documents/${ref}` };
    case "customer": return { to: `/clients/${ref}` };
    case "ticket": return { to: `/support/tickets/${ref}` };
    case "product_item": return { to: `/produit/${ref}` };
    case "purchase_request": return { to: `/achats/demandes/${ref}` };
    default: return { to: "/" };
  }
}

export function governanceError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.msg) return detail.msg;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail)) return "Vérifiez les champs saisis.";
  return fallback;
}

export function formatDay(value) {
  if (!value) return "";
  const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return value;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

export const todayIso = () => new Date().toISOString().slice(0, 10);

// Words for the state chip: « non tranchée » stays visible on a deferred decision, « à revoir » once its date has come.
export function decisionChips(decision) {
  const chips = [];
  if (decision.to_review) chips.push({ id: "to_review", label: `À revoir${decision.review_on ? ` depuis le ${formatDay(decision.review_on)}` : ""}`, tone: "amber" });
  else if (decision.status === "deferred") chips.push({ id: "undecided", label: `Non tranchée${decision.review_on ? ` · revue le ${formatDay(decision.review_on)}` : ""}`, tone: "amber" });
  if (decision.superseded) chips.push({ id: "superseded", label: "Remplacée", tone: "slate" });
  return chips;
}

export const emptyOption = () => ({ id: "", label: "", pros: "", cons: "" });

export function emptyDecisionForm(decision) {
  return {
    title: decision?.title || "", context: decision?.context || "",
    options: (decision?.options || []).map((o) => ({ id: o.id, label: o.label, pros: o.pros || "", cons: o.cons || "" })),
    deciders: (decision?.deciders || []).map((d) => d.id),
    interested: (decision?.interested || []).map((p) => (p.type === "external" ? { type: "external", name: p.name } : { type: p.type, id: p.id, name: p.name })),
    links: (decision?.links || []).map((l) => ({ kind: l.kind, ref_id: l.ref_id, visible: l.visible, label: l.label })),
    restricted: !!decision?.restricted,
  };
}

export function decisionProblem(form) {
  if ((form.title || "").trim().length < 3) return "Donnez un titre à la décision (3 caractères au moins).";
  if (form.options.some((o) => !o.label.trim())) return "Chaque option a un libellé.";
  return "";
}

export function decisionPayload(form) {
  return {
    title: form.title.trim(), context: form.context.trim() || null,
    options: form.options.filter((o) => o.label.trim()).map((o) => ({ ...(o.id ? { id: o.id } : {}), label: o.label.trim(), pros: o.pros.trim() || null, cons: o.cons.trim() || null })),
    deciders: form.deciders.map((id) => ({ type: "account", id })),
    interested: form.interested.map((p) => (p.type === "external" ? { type: "external", name: p.name } : { type: p.type, id: p.id })),
    links: form.links.map((l) => ({ kind: l.kind, ref_id: l.ref_id })), restricted: !!form.restricted,
  };
}

// --- Meetings (22-2) -----------------------------------------------------------------------------------------------------
export const canManageMeetings = (user) => canSeeGovernance(user) && hasPermission(user, "governance:manage_meetings");

export const MEETING_STATUS_LABELS = { planned: "Prévue", held: "En cours", closed: "Close", cancelled: "Annulée" };
export const MEETING_STATUS_TONES = { planned: "sky", held: "amber", closed: "emerald", cancelled: "slate" };
export const MEETING_VIEW_TABS = [["upcoming", "À venir"], ["closed", "Closes"], ["all", "Toutes"]];
export const MEETING_KINDS = [["direction", "Direction"], ["commercial", "Commercial"], ["product", "Produit"], ["support", "Support"], ["operations", "Opérations"], ["other", "Autre"]];
export const MEETING_ACTION_LABELS = { hold: "Ouvrir la réunion", close: "Clore la réunion", cancel: "Annuler la réunion" };
export const MEETING_ACTION_HINTS = {
  hold: "La réunion a lieu : notez la présence et rédigez le compte rendu.",
  close: "Le compte rendu est figé. Une correction ultérieure sera un addendum daté, jamais une réécriture.",
  cancel: "La réunion n'aura pas lieu. Elle reste dans la liste, marquée annulée.",
};
export const PRESENCE_LABELS = { expected: "Attendu", present: "Présent", excused: "Excusé", absent: "Absent" };
export const PARTICIPANT_ROLE_LABELS = { organizer: "Organisateur", secretary: "Secrétaire", participant: "Participant" };
export const MEETING_HISTORY_LABELS = {
  meeting_created: "Réunion créée", meeting_updated: "Réunion modifiée", meeting_hold: "Réunion ouverte", meeting_close: "Réunion close",
  meeting_cancel: "Réunion annulée", meeting_addendum: "Addendum ajouté", task_linked: "Action liée", task_unlinked: "Action retirée",
  file_added: "Fichier joint", file_removed: "Fichier retiré",
};

// The minutes save on their own while they are written, so one person's run of edits makes a run of « Réunion modifiée » lines in the
// journal. Shown as one line (the journal itself keeps every entry): consecutive edits by the same person, newest first as received.
export function groupMeetingHistory(entries) {
  const out = [];
  for (const h of entries || []) {
    const last = out[out.length - 1];
    if (h.kind === "meeting_updated" && last?.kind === "meeting_updated" && last.actor_name === h.actor_name) {
      last.count += 1;
      last.first_at = h.at;
    } else {
      out.push({ ...h, count: 1, first_at: h.at });
    }
  }
  return out;
}

// The text as the server keeps it (rules.clean_text): no surrounding space, no trailing blanks, \n line breaks. Comparing through it
// keeps a trailing space being typed from counting as an unsaved change once the server has stored the text without it.
export const cleanMinutes = (value) => (value || "").replace(/\r\n/g, "\n").trim().split("\n").map((line) => line.trimEnd()).join("\n");

export const meetingKindLabel = (code) => (MEETING_KINDS.find(([id]) => id === code) || [])[1] || code;

// « 10 nov. 2026 · 14:30 » from the stored « AAAA-MM-JJThh:mm » (UTC), read in the zone of the person (lib/time.js).
export function formatMoment(value, zone = getActiveZone()) {
  if (!value) return "";
  const [day, time] = String(utcToLocal(value, zone)).split("T");
  return `${formatDay(day)}${time ? ` · ${time}` : ""}`;
}

export function emptyMeetingForm(meeting, zone = getActiveZone()) {
  return {
    title: meeting?.title || "", kind: meeting?.kind || "", starts_at: utcToLocal(meeting?.starts_at, zone) || "", ends_at: utcToLocal(meeting?.ends_at, zone) || "", place: meeting?.place || "",
    video_url: meeting?.video_url || "",
    participants: (meeting?.participants || []).map((p) => ({ type: p.type, ...(p.type === "external" ? { name: p.name } : { id: p.id, name: p.name }), role: p.role, presence: p.presence })),
    agenda: (meeting?.agenda || []).map((a) => ({ id: a.id, title: a.title, note: a.note || "" })),
    restricted: !!meeting?.restricted,
    links: (meeting?.links || []).map((l) => ({ kind: l.kind, ref_id: l.ref_id, visible: l.visible, label: l.label })),
  };
}

export function meetingProblem(form) {
  if ((form.title || "").trim().length < 3) return "Donnez un objet à la réunion (3 caractères au moins).";
  if (!form.kind) return "Choisissez un type de réunion.";
  if (!form.starts_at) return "Indiquez la date et l'heure.";
  if (form.ends_at && form.ends_at <= form.starts_at) return "La fin doit suivre le début.";
  if (form.video_url.trim() && !/^https:\/\//.test(form.video_url.trim())) return "Le lien de visioconférence doit commencer par https://.";
  if (form.agenda.some((a) => !a.title.trim())) return "Chaque point de l'ordre du jour a un titre.";
  return "";
}

export function meetingPayload(form, zone = getActiveZone()) {
  return {
    title: form.title.trim(), kind: form.kind, starts_at: localToUtc(form.starts_at, zone), ends_at: form.ends_at ? localToUtc(form.ends_at, zone) : null, place: form.place.trim() || null, video_url: form.video_url.trim() || null,
    participants: form.participants.map((p) => ({ type: p.type, ...(p.type === "external" ? { name: p.name } : { id: p.id }), role: p.role || "participant", presence: p.presence || "expected" })),
    agenda: form.agenda.filter((a) => a.title.trim()).map((a) => ({ ...(a.id ? { id: a.id } : {}), title: a.title.trim(), note: a.note.trim() || null })),
    restricted: !!form.restricted, links: form.links.map((l) => ({ kind: l.kind, ref_id: l.ref_id })),
  };
}

// The organiser is one account among the participants: choosing one makes them organiser, adding them first when they are not listed yet.
export function withOrganizer(participants, accountId, people) {
  const rest = participants.map((p) => (p.role === "organizer" ? { ...p, role: "participant" } : p));
  if (!accountId) return rest;
  if (rest.some((p) => p.type === "account" && p.id === accountId)) return rest.map((p) => (p.type === "account" && p.id === accountId ? { ...p, role: "organizer" } : p));
  return [{ type: "account", id: accountId, name: people.find((x) => x.id === accountId)?.name, role: "organizer", presence: "expected" }, ...rest];
}

// People picked in the shared « concerned people » field keep the role and the presence they already had.
export function mergeParticipants(previous, picked) {
  const key = (p) => (p.type === "external" ? `x:${p.name.toLowerCase()}` : `${p.type}:${p.id}`);
  const known = new Map(previous.map((p) => [key(p), p]));
  return picked.map((p) => known.get(key(p)) || { ...p, role: "participant", presence: "expected" });
}

export const emptyAgendaItem = () => ({ id: "", title: "", note: "" });

// --- Risks (22-3) --------------------------------------------------------------------------------------------------------
export const canManageRisks = (user) => canSeeGovernance(user) && hasPermission(user, "governance:manage_risks");

export const RISK_CATEGORIES = [
  ["legal", "Juridique"], ["financial", "Financier"], ["product", "Produit"], ["security", "Sécurité"], ["commercial", "Commercial"],
  ["operational", "Opérationnel"], ["people", "Ressources humaines"], ["reputation", "Réputation"],
];
export const riskCategoryLabel = (code) => (RISK_CATEGORIES.find(([id]) => id === code) || [])[1] || code;
export const IMPACT_LABELS = { 1: "Mineur", 2: "Modéré", 3: "Majeur", 4: "Critique" };
export const PROBABILITY_LABELS = { 1: "Improbable", 2: "Possible", 3: "Probable", 4: "Quasi certain" };
export const LEVEL_LABELS = { low: "Faible", medium: "Moyen", high: "Élevé", critical: "Critique" };
export const LEVEL_TONES = { low: "emerald", medium: "sky", high: "amber", critical: "red" };
// Cell colours of the impact x probability grid, by the same thresholds as the server (low <= 3, medium 4-6, high 8-9, critical >= 12).
export const levelOf = (impact, probability) => { const s = impact * probability; return s >= 12 ? "critical" : s >= 8 ? "high" : s >= 4 ? "medium" : "low"; };
export const LEVEL_CELL = { low: "bg-emerald-50 text-emerald-800", medium: "bg-sky-50 text-sky-800", high: "bg-amber-100 text-amber-900", critical: "bg-red-100 text-red-800" };
export const RISK_STATUS_LABELS = { open: "Ouvert", accepted: "Accepté", closed: "Clos" };
export const RISK_STATUS_TONES = { open: "sky", accepted: "slate", closed: "slate" };
export const RISK_VIEW_TABS = [["open", "Ouverts"], ["closed", "Acceptés et clos"], ["all", "Tous"]];
export const RISK_ACTION_LABELS = { review: "Marquer comme revu", accept: "Accepter le risque", close: "Clore le risque", reopen: "Rouvrir le risque" };
export const RISK_ACTION_HINTS = {
  review: "Confirmez ou révisez l'évaluation. L'ancienne reste dans l'historique et la prochaine revue est reportée d'une période.",
  accept: "Vous décidez de vivre avec ce risque, sans parade supplémentaire. Il ne revient plus dans les revues.",
  close: "Le risque a disparu ou est traité. Il reste consultable mais n'est plus modifiable.",
  reopen: "Le risque redevient ouvert et sera revu dans une période complète.",
};
export const RISK_REASON_ACTIONS = ["accept", "close", "reopen"];
export const RISK_HISTORY_LABELS = {
  risk_created: "Risque consigné", risk_updated: "Risque modifié", risk_reviewed: "Risque revu", risk_accept: "Risque accepté", risk_close: "Risque clos",
  risk_reopen: "Risque rouvert", task_linked: "Action liée", task_unlinked: "Action retirée",
};
export const REVIEW_PERIODS = [[1, "Tous les mois"], [3, "Tous les 3 mois"], [6, "Tous les 6 mois"], [12, "Tous les ans"]];

export const emptyMeasure = () => ({ id: "", text: "", status: "todo" });

export function emptyRiskForm(risk) {
  return {
    title: risk?.title || "", description: risk?.description || "", category: risk?.category || "", impact: risk?.impact || 0, probability: risk?.probability || 0,
    owner_id: risk?.owner?.id || "", review_months: risk?.review_months || 3,
    measures: (risk?.measures || []).map((m) => ({ id: m.id, text: m.text, status: m.status })),
    restricted: !!risk?.restricted, links: (risk?.links || []).map((l) => ({ kind: l.kind, ref_id: l.ref_id, visible: l.visible, label: l.label })),
    decision_ids: (risk?.decisions || []).map((d) => d.id),
  };
}

export function riskProblem(form) {
  if ((form.title || "").trim().length < 3) return "Donnez un titre au risque (3 caractères au moins).";
  if (!form.category) return "Choisissez une catégorie.";
  if (!form.impact) return "Évaluez l'impact.";
  if (!form.probability) return "Évaluez la probabilité.";
  if (!form.owner_id) return "Nommez le propriétaire du risque.";
  if (form.measures.some((m) => !m.text.trim())) return "Chaque mesure a un texte.";
  return "";
}

export function riskPayload(form) {
  return {
    title: form.title.trim(), description: form.description.trim() || null, category: form.category, impact: form.impact, probability: form.probability, owner_id: form.owner_id,
    review_months: form.review_months, measures: form.measures.filter((m) => m.text.trim()).map((m) => ({ ...(m.id ? { id: m.id } : {}), text: m.text.trim(), status: m.status })),
    restricted: !!form.restricted, links: form.links.map((l) => ({ kind: l.kind, ref_id: l.ref_id })), decision_ids: form.decision_ids,
  };
}

// The impact x probability grid: every cell, with the count of open risks it holds (the server sends the non-empty ones only).
export function matrixCells(matrix) {
  const counts = new Map((matrix || []).map((c) => [`${c.impact}:${c.probability}`, c.count]));
  const cells = [];
  for (let impact = 4; impact >= 1; impact -= 1) {
    for (let probability = 1; probability <= 4; probability += 1) cells.push({ impact, probability, count: counts.get(`${impact}:${probability}`) || 0, level: levelOf(impact, probability) });
  }
  return cells;
}
